import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useClientModal } from '@shared/clientModalContext';
import { 
  Plus, 
  Search, 
  Building2, 
  Sparkles, 
  List, 
  Grid3x3, 
  Map, 
  MapPin, 
  X,
  Edit,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Phone,
  Calendar,
  Droplet,
  Filter,
  Navigation,
  AlertCircle,
  Loader2,
  ChevronDown,
  Upload,
  Settings,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { clientsAPI, cspireAPI, franklinAPI } from '@shared/apiClient';
import {
  syncActiveClients,
  getClientsSnapshotData,
  getClientsSnapshotUpdatedAt,
  clearClientsSnapshot
} from '@shared/clientsSync';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate } from '@shared/utils';
import CityCleanupModal from '../components/CityCleanupModal';
import AddressCleanupModal from '../components/AddressCleanupModal';
import ClientDetailsModal from '../components/ClientDetailsModal';
import CspireSiteDetailsModal from '../components/CspireSiteDetailsModal';
import FranklinSiteDetailsModal from '../components/FranklinSiteDetailsModal';
import ClientsMap from '../components/ClientsMap';

export default function Clients() {
  const queryClient = useQueryClient();
  const [customerTab, setCustomerTab] = useState('active');
  const [deactivatedPage, setDeactivatedPage] = useState(1);
  const [deactivatedSearch, setDeactivatedSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'grouped', 'map'
  const [sortField, setSortField] = useState('contactLastName');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('all'); // Load all clients by default
  const [contractFilter, setContractFilter] = useState('all'); // 'all', 'contract', 'annual', 'non-contract'
  const [selectedServiceMonthPair, setSelectedServiceMonthPair] = useState(null); // null, 'annual', or [month1, month2] array
  const [cspireSiteSearch, setCspireSiteSearch] = useState('');
  const [cspireIncludeInactive, setCspireIncludeInactive] = useState(false);
  const [cspireSiteModalMode, setCspireSiteModalMode] = useState(null); // null | 'create' | 'edit'
  const [isCspireDetailsModalOpen, setIsCspireDetailsModalOpen] = useState(false);
  const [selectedCspireSite, setSelectedCspireSite] = useState(null);
  const [cspireFormData, setCspireFormData] = useState({
    siteId: '',
    locationName: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
    isActive: true
  });
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [isAddressCleanupModalOpen, setIsAddressCleanupModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const { openCreateModal, openEditModal } = useClientModal();
  const [isAdvancedToolsOpen, setIsAdvancedToolsOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, estimatedTotal: null });
  const isCspireTab = viewMode === 'table' && selectedServiceMonthPair === 'cspire';
  const [franklinSiteSearch, setFranklinSiteSearch] = useState('');
  const [franklinSiteModalMode, setFranklinSiteModalMode] = useState(null); // null | 'create' | 'edit'
  const [isFranklinDetailsModalOpen, setIsFranklinDetailsModalOpen] = useState(false);
  const [selectedFranklinSite, setSelectedFranklinSite] = useState(null);
  const [franklinFormData, setFranklinFormData] = useState({
    siteId: '',
    company: '',
    locationName: '',
    address: '',
    city: '',
    district: '',
    county: '',
    state: '',
    zipCode: '',
    notes: '',
    isActive: true
  });
  const isFranklinTab = viewMode === 'table' && selectedServiceMonthPair === 'franklin';

  // Pending clients
  const { data: pendingClientsData } = useQuery(
    ['pendingClients'],
    () => clientsAPI.getPendingClients(),
    { refetchInterval: 30000 }
  );
  const pendingClients = pendingClientsData?.data?.clients || [];

  const { data: inactiveClientsData, isLoading: inactiveClientsLoading } = useQuery(
    ['inactiveClients', deactivatedPage, deactivatedSearch],
    () => clientsAPI.getClients({
      page: deactivatedPage,
      limit: 20,
      isActive: false,
      search: deactivatedSearch || undefined,
    }),
    { keepPreviousData: true, enabled: customerTab === 'deactivated' }
  );

  const reactivateClientMutation = useMutation(clientsAPI.reactivateClient, {
    onSuccess: () => {
      queryClient.invalidateQueries('inactiveClients');
      queryClient.invalidateQueries('clients');
      queryClient.invalidateQueries('allClients');
      toast.success('Customer reactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reactivate customer');
    },
  });

  const handleReactivateClient = (clientId, companyName) => {
    if (window.confirm(`Are you sure you want to reactivate ${companyName}?`)) {
      reactivateClientMutation.mutate(clientId);
    }
  };

  const approveMutation = useMutation(
    (id) => clientsAPI.approveClient(id),
    {
      onSuccess: () => {
        toast.success('Client approved!');
        queryClient.invalidateQueries('pendingClients');
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve client');
      }
    }
  );

  const rejectMutation = useMutation(
    (id) => clientsAPI.rejectClient(id),
    {
      onSuccess: () => {
        toast.success('Pending client rejected');
        queryClient.invalidateQueries('pendingClients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject client');
      }
    }
  );

  // Import mutation
  const importMutation = useMutation(
    (file) => clientsAPI.importClients(file),
    {
      onSuccess: (response) => {
        const results = response.data.results;
        toast.success(
          `Import completed: ${results.created} created, ${results.updated} updated, ${results.skipped} skipped`
        );
        if (results.errors.length > 0) {
          console.error('Import errors:', results.errors);
          toast.error(`${results.errors.length} errors occurred during import`);
        }
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
        setIsImportModalOpen(false);
        setImportFile(null);
        setIsImporting(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to import customers');
        setIsImporting(false);
      }
    }
  );

  const handleImportClick = () => {
    setIsImportModalOpen(true);
    setIsAdvancedToolsOpen(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportSubmit = () => {
    if (!importFile) {
      toast.error('Please select a file');
      return;
    }
    setIsImporting(true);
    importMutation.mutate(importFile);
  };

  const handleOpenCspireCreate = () => {
    setSelectedCspireSite(null);
    setCspireFormData({
      siteId: '',
      locationName: '',
      addressLine1: '',
      city: '',
      state: '',
      zipCode: '',
      latitude: '',
      longitude: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      notes: '',
      isActive: true
    });
    setCspireSiteModalMode('create');
  };

  const handleOpenCspireEdit = (site) => {
    setSelectedCspireSite(site);
    setCspireFormData({
      siteId: site.siteId || '',
      locationName: site.locationName || '',
      addressLine1: site.addressLine1 || '',
      city: site.city || '',
      state: site.state || '',
      zipCode: site.zipCode || '',
      latitude: site.latitude ?? '',
      longitude: site.longitude ?? '',
      contactName: site.contactName || '',
      contactEmail: site.contactEmail || '',
      contactPhone: site.contactPhone || '',
      notes: site.notes || '',
      isActive: site.isActive !== false
    });
    setCspireSiteModalMode('edit');
  };

  const handleCloseCspireSiteModal = () => {
    setCspireSiteModalMode(null);
    setSelectedCspireSite(null);
  };

  const handleOpenFranklinCreate = () => {
    setSelectedFranklinSite(null);
    setFranklinFormData({
      siteId: '',
      company: '',
      locationName: '',
      address: '',
      city: '',
      district: '',
      county: '',
      state: '',
      zipCode: '',
      notes: '',
      isActive: true
    });
    setFranklinSiteModalMode('create');
  };

  const handleOpenFranklinEdit = (site) => {
    setSelectedFranklinSite(site);
    setFranklinFormData({
      siteId: site.siteId || '',
      company: site.company || '',
      locationName: site.locationName || '',
      address: site.address || '',
      city: site.city || '',
      district: site.district || '',
      county: site.county || '',
      state: site.state || '',
      zipCode: site.zipCode || '',
      notes: site.notes || '',
      isActive: site.isActive !== false
    });
    setFranklinSiteModalMode('edit');
  };

  const handleCloseFranklinSiteModal = () => {
    setFranklinSiteModalMode(null);
    setSelectedFranklinSite(null);
  };

  // Geocode button component for dropdown
  const GeocodeButtonDropdown = () => {
    const geocodeMutation = useMutation(
      () => clientsAPI.geocodeAllClients(),
      {
        onSuccess: (data) => {
          const result = data.data;
          const approxNote = result.approximate ? ` ${result.approximate} approximate.` : '';
          const correctedNote = result.corrected ? ` ${result.corrected} had bad zip/state data auto-corrected.` : '';
          const remainingNote = result.ranOutOfTime ? ` ${result.remaining} more queued — click Geocode Addresses again to continue.` : '';
          toast.success(`Geocoded ${result.geocoded} customer(s).${approxNote}${correctedNote} ${result.failed} failed.${remainingNote}`, {
            duration: result.ranOutOfTime ? 8000 : 4000
          });
          queryClient.invalidateQueries('clients');
          queryClient.invalidateQueries('geocodingFailures');
          setIsGeocoding(false);
          setIsAdvancedToolsOpen(false);
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to geocode customers');
          setIsGeocoding(false);
        }
      }
    );

    const handleGeocode = () => {
      if (window.confirm('This will geocode all customers without coordinates. This may take several minutes. Continue?')) {
        setIsGeocoding(true);
        geocodeMutation.mutate();
      }
    };

    return (
      <button
        onClick={handleGeocode}
        disabled={isGeocoding || geocodeMutation.isLoading}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50"
      >
        {isGeocoding || geocodeMutation.isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Geocoding...
          </>
        ) : (
          <>
            <Navigation className="h-4 w-4 mr-2" />
            Geocode Addresses
          </>
        )}
      </button>
    );
  };

  // Geocoding failures list component
  const GeocodingFailuresList = () => {
    const { data: failuresData, isLoading: isLoadingFailures } = useQuery(
      'geocodingFailures',
      () => clientsAPI.getGeocodingFailures(),
      { enabled: true }
    );

    const failures = failuresData?.data?.clients || [];

    if (isLoadingFailures || failures.length === 0) {
      return null;
    }

    return (
      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Geocoding Failures ({failures.length})
              </h3>
            </div>
            <button
              onClick={() => setIsAddressCleanupModalOpen(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View Details
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Customers that could not be geocoded or were only located approximately. Please check and fix their addresses.
          </p>
        </div>
        <div className="px-4 py-3 sm:px-6">
          <div className="space-y-2">
            {failures.slice(0, 5).map((client) => {
              const displayName = client.isCommercial && client.companyName 
                ? client.companyName 
                : `${client.contactFirstName} ${client.contactLastName}`;
              return (
                <div key={client.id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-gray-900">{displayName}</span>
                  <span className="text-gray-500 ml-2">
                    {client.address}, {client.city}, {client.state} {client.zipCode || ''}
                  </span>
                  {client.geocodeAccuracy && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {client.geocodeAccuracy === 'zip' ? 'ZIP-level only' : 'City-level only'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleViewClick(client)}
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  View
                </button>
              </div>
              );
            })}
            {failures.length > 5 && (
              <p className="text-xs text-gray-500 mt-2">
                ...and {failures.length - 5} more. Click "View Details" to see all.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Downloads all active clients once, then only fetches rows changed since the
  // last sync (updatedSince delta). The snapshot is persisted to localStorage so
  // the page renders instantly even after a full app reload.
  const fetchAllClients = () => syncActiveClients(setLoadingProgress);

  // Shared cache options: render cached data immediately, refresh in the background.
  const clientsQueryOptions = {
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    initialData: getClientsSnapshotData,
    initialDataUpdatedAt: getClientsSnapshotUpdatedAt
  };

  // Fetch all clients (for grouped and map views)
  const { data: allClientsData, isLoading: isLoadingAll, isFetching: isFetchingAll } = useQuery(
    'allClients',
    fetchAllClients,
    { ...clientsQueryOptions, enabled: viewMode !== 'table' }
  );

  // Fetch all clients (for table view) - always load all clients for fast tab switching
  const { data: paginatedData, isLoading: isLoadingPaginated, isFetching: isFetchingPaginated } = useQuery(
    'clients',
    fetchAllClients,
    {
      ...clientsQueryOptions,
      keepPreviousData: true,
      enabled: viewMode === 'table'
    }
  );

  const isLoading = viewMode === 'table' ? isLoadingPaginated : isLoadingAll;
  const isRefreshingClients = viewMode === 'table' ? isFetchingPaginated : isFetchingAll;
  const { data: cspireSitesData, isLoading: isCspireSitesLoading } = useQuery(
    ['cspireSites', cspireSiteSearch, cspireIncludeInactive],
    () => cspireAPI.getSites({
      search: cspireSiteSearch,
      includeInactive: cspireIncludeInactive
    }),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      enabled: isCspireTab
    }
  );

  const { data: franklinSitesData, isLoading: isFranklinSitesLoading } = useQuery(
    ['franklinSites', franklinSiteSearch],
    () => franklinAPI.getSites({ search: franklinSiteSearch }),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      enabled: isFranklinTab
    }
  );

  // Get clients based on view mode
  const clients = useMemo(() => {
    if (viewMode === 'table') {
      return paginatedData?.clients || paginatedData?.data?.clients || [];
    } else {
      return allClientsData?.clients || allClientsData?.data?.clients || [];
    }
  }, [viewMode, paginatedData, allClientsData]);

  const cspireSites = cspireSitesData?.data?.sites || [];
  const franklinSites = franklinSitesData?.data?.sites || [];

  const { data: cspireSiteWorkOrdersData } = useQuery(
    ['cspireSiteWorkOrders', selectedCspireSite?.id],
    () => cspireAPI.getWorkOrders({ siteId: selectedCspireSite.id }),
    { enabled: isCspireDetailsModalOpen && !!selectedCspireSite?.id }
  );
  const cspireSiteWorkOrders = cspireSiteWorkOrdersData?.data?.workOrders || [];

  const cspireCreateMutation = useMutation(
    (payload) => cspireAPI.createSite(payload),
    {
      onSuccess: () => {
        toast.success('CSpire site created');
        queryClient.invalidateQueries('cspireSites');
        handleCloseCspireSiteModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create CSpire site');
      }
    }
  );

  const cspireUpdateMutation = useMutation(
    ({ id, payload }) => cspireAPI.updateSite(id, payload),
    {
      onSuccess: () => {
        toast.success('CSpire site updated');
        queryClient.invalidateQueries('cspireSites');
        handleCloseCspireSiteModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update CSpire site');
      }
    }
  );

  const handleCspireSiteFormSubmit = (e) => {
    e.preventDefault();

    const trimmedSiteId = cspireFormData.siteId.trim();
    if (!trimmedSiteId) {
      toast.error('Site ID is required');
      return;
    }

    const latitudeValue = cspireFormData.latitude === '' ? null : parseFloat(cspireFormData.latitude);
    const longitudeValue = cspireFormData.longitude === '' ? null : parseFloat(cspireFormData.longitude);

    if (cspireFormData.latitude !== '' && Number.isNaN(latitudeValue)) {
      toast.error('Latitude must be a valid number');
      return;
    }
    if (cspireFormData.longitude !== '' && Number.isNaN(longitudeValue)) {
      toast.error('Longitude must be a valid number');
      return;
    }
    if (latitudeValue !== null && (latitudeValue < -90 || latitudeValue > 90)) {
      toast.error('Latitude must be between -90 and 90');
      return;
    }
    if (longitudeValue !== null && (longitudeValue < -180 || longitudeValue > 180)) {
      toast.error('Longitude must be between -180 and 180');
      return;
    }

    const payload = {
      siteId: trimmedSiteId,
      locationName: cspireFormData.locationName.trim() || null,
      addressLine1: cspireFormData.addressLine1.trim() || null,
      city: cspireFormData.city.trim() || null,
      state: cspireFormData.state.trim() || null,
      zipCode: cspireFormData.zipCode.trim() || null,
      latitude: latitudeValue,
      longitude: longitudeValue,
      contactName: cspireFormData.contactName.trim() || null,
      contactEmail: cspireFormData.contactEmail.trim() || null,
      contactPhone: cspireFormData.contactPhone.trim() || null,
      notes: cspireFormData.notes.trim() || null,
      isActive: !!cspireFormData.isActive
    };

    if (cspireSiteModalMode === 'create') {
      cspireCreateMutation.mutate(payload);
    } else {
      cspireUpdateMutation.mutate({ id: selectedCspireSite.id, payload });
    }
  };

  const franklinCreateMutation = useMutation(
    (payload) => franklinAPI.createSite(payload),
    {
      onSuccess: () => {
        toast.success('Franklin site created');
        queryClient.invalidateQueries('franklinSites');
        handleCloseFranklinSiteModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create Franklin site');
      }
    }
  );

  const franklinUpdateMutation = useMutation(
    ({ id, payload }) => franklinAPI.updateSite(id, payload),
    {
      onSuccess: () => {
        toast.success('Franklin site updated');
        queryClient.invalidateQueries('franklinSites');
        handleCloseFranklinSiteModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update Franklin site');
      }
    }
  );

  const handleFranklinSiteFormSubmit = (e) => {
    e.preventDefault();

    const trimmedSiteId = franklinFormData.siteId.trim();
    if (!trimmedSiteId) {
      toast.error('Site ID is required');
      return;
    }

    const payload = {
      siteId: trimmedSiteId,
      company: franklinFormData.company.trim() || null,
      locationName: franklinFormData.locationName.trim() || null,
      address: franklinFormData.address.trim() || null,
      city: franklinFormData.city.trim() || null,
      district: franklinFormData.district.trim() || null,
      county: franklinFormData.county.trim() || null,
      state: franklinFormData.state.trim() || null,
      zipCode: franklinFormData.zipCode.trim() || null,
      notes: franklinFormData.notes.trim() || null,
      isActive: !!franklinFormData.isActive
    };

    if (franklinSiteModalMode === 'create') {
      franklinCreateMutation.mutate(payload);
    } else {
      franklinUpdateMutation.mutate({ id: selectedFranklinSite.id, payload });
    }
  };

  // Filter clients by search term, contract filter, and service month pair
  const filteredClients = useMemo(() => {
    let filtered = clients;
    
    // Apply contract filter
    if (contractFilter === 'contract') {
      // Contract clients with 2 service months (semi-annual)
      filtered = filtered.filter(client => 
        client.isContractClient === true && 
        client.serviceCycleMonths && 
        client.serviceCycleMonths.length === 2
      );
    } else if (contractFilter === 'annual') {
      // Annual contract clients with only 1 service month
      filtered = filtered.filter(client => 
        client.isContractClient === true && 
        client.serviceCycleMonths && 
        client.serviceCycleMonths.length === 1
      );
    } else if (contractFilter === 'non-contract') {
      filtered = filtered.filter(client => client.isContractClient !== true);
    }
    
    // Apply service month pair filter
    if (selectedServiceMonthPair && viewMode === 'table') {
      if (selectedServiceMonthPair === 'cspire' || selectedServiceMonthPair === 'franklin') {
        return [];
      }
      if (selectedServiceMonthPair === 'annual') {
        // Filter for annual clients (1 service month)
        filtered = filtered.filter(client => {
          return client.isContractClient && 
                 client.serviceCycleMonths && 
                 client.serviceCycleMonths.length === 1;
        });
      } else if (selectedServiceMonthPair === 'commercial') {
        // Filter for commercial clients
        filtered = filtered.filter(client => client.isCommercial === true);
      } else {
        // Filter for specific month pair (include clients with either or both months)
        const [month1, month2] = selectedServiceMonthPair;
        const selectedMonths = [month1, month2].sort((a, b) => a - b);
        filtered = filtered.filter(client => {
          if (!client.isContractClient || !client.serviceCycleMonths || client.serviceCycleMonths.length === 0) {
            return false;
          }
          const clientMonths = client.serviceCycleMonths;
          if (clientMonths.length === 1) {
            return selectedMonths.includes(clientMonths[0]);
          }
          if (clientMonths.length === 2) {
            const sortedClientMonths = [...clientMonths].sort((a, b) => a - b);
            return sortedClientMonths[0] === selectedMonths[0] && sortedClientMonths[1] === selectedMonths[1];
          }
          return false;
        });
      }
    }
    
    // Apply search filter (client-side since we load all data)
    if (!searchTerm.trim()) return filtered;
    
    const query = searchTerm.toLowerCase();
    return filtered.filter(client => {
      const companyName = (client.companyName || '').toLowerCase();
      const contactName = `${client.contactFirstName || ''} ${client.contactLastName || ''}`.toLowerCase();
      const email = (client.email || '').toLowerCase();
      const city = (client.city || '').toLowerCase();
      const state = (client.state || '').toLowerCase();
      const address = (client.address || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      
      return companyName.includes(query) ||
             contactName.includes(query) ||
             email.includes(query) ||
             city.includes(query) ||
             state.includes(query) ||
             address.includes(query) ||
             phone.includes(query);
    });
  }, [clients, searchTerm, viewMode, contractFilter, selectedServiceMonthPair]);

  // Sort clients (for all views - client-side sorting)
  const sortedClients = useMemo(() => {
    
    const sorted = [...filteredClients];
    sorted.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      // Handle nested fields
      if (sortField === 'contactName') {
        aVal = `${a.contactFirstName} ${a.contactLastName}`;
        bVal = `${b.contactFirstName} ${b.contactLastName}`;
      }
      if (sortField === 'contactLastName') {
        aVal = a.contactLastName || '';
        bVal = b.contactLastName || '';
      }
      
      // Convert to string for comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
    
    return sorted;
  }, [filteredClients, sortField, sortDirection, viewMode]);

  // Group clients by city (for grouped view)
  const clientsByCity = useMemo(() => {
    if (viewMode !== 'grouped') return [];
    
    const grouped = {};
    
    sortedClients.forEach(client => {
      const locationKey = `${client.city}, ${client.state}`;
      if (!grouped[locationKey]) {
        grouped[locationKey] = {
          city: client.city,
          state: client.state,
          clients: []
        };
      }
      grouped[locationKey].clients.push(client);
    });

    return Object.values(grouped).sort((a, b) => {
      if (a.state !== b.state) return a.state.localeCompare(b.state);
      return a.city.localeCompare(b.city);
    });
  }, [sortedClients, viewMode]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreateClick = () => {
    openCreateModal();
  };

  const handleEditClick = (client) => {
    openEditModal(client);
  };

  const handleViewClick = (client) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const handleEditFromDetails = (client) => {
    openEditModal(client);
  };

  // Update oil change month mutation with optimistic updates
  const updateOilChangeMonthMutation = useMutation(
    ({ clientId, month }) => clientsAPI.updateClient(clientId, { oilChangeMonth: month }),
    {
      onMutate: async ({ clientId, month }) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries('clients');
        await queryClient.cancelQueries('allClients');
        await queryClient.cancelQueries(['client', clientId]);

        // Snapshot the previous value
        const previousClients = queryClient.getQueryData('clients');
        const previousAllClients = queryClient.getQueryData('allClients');
        const previousClient = queryClient.getQueryData(['client', clientId]);

        // Optimistically update the cache
        queryClient.setQueryData('clients', (old) => {
          if (!old) return old;
          const data = old.clients || old.data?.clients || [];
          return {
            ...old,
            clients: data.map(client => 
              client.id === clientId ? { ...client, oilChangeMonth: month } : client
            ),
            data: old.data ? {
              ...old.data,
              clients: (old.data.clients || []).map(client => 
                client.id === clientId ? { ...client, oilChangeMonth: month } : client
              )
            } : undefined
          };
        });

        queryClient.setQueryData('allClients', (old) => {
          if (!old) return old;
          return {
            ...old,
            clients: (old.clients || []).map(client => 
              client.id === clientId ? { ...client, oilChangeMonth: month } : client
            )
          };
        });

        queryClient.setQueryData(['client', clientId], (old) => {
          if (!old) return old;
          const client = old.client || old.data?.client || old;
          return {
            ...old,
            client: client.id === clientId ? { ...client, oilChangeMonth: month } : client,
            data: old.data ? {
              ...old.data,
              client: old.data.client?.id === clientId ? { ...old.data.client, oilChangeMonth: month } : old.data.client
            } : undefined
          };
        });

        return { previousClients, previousAllClients, previousClient };
      },
      onError: (error, variables, context) => {
        // Rollback on error
        if (context?.previousClients) {
          queryClient.setQueryData('clients', context.previousClients);
        }
        if (context?.previousAllClients) {
          queryClient.setQueryData('allClients', context.previousAllClients);
        }
        if (context?.previousClient) {
          queryClient.setQueryData(['client', variables.clientId], context.previousClient);
        }
        toast.error(error.response?.data?.message || 'Failed to update oil change month');
      },
      onSuccess: () => {
        toast.success('Oil change month updated successfully');
        // Invalidate to ensure we have the latest data
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onSettled: () => {
        // Always refetch after error or success to ensure consistency
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      }
    }
  );

  const handleMonthClick = (client, month, e) => {
    e.stopPropagation(); // Prevent row click
    
    // If clicking the same month that's already the oil change month, do nothing
    if (client.oilChangeMonth === month) {
      return;
    }
    
    // Update the oil change month
    updateOilChangeMonthMutation.mutate({ clientId: client.id, month });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  if (isLoading) {
    const progress = loadingProgress.total > 0 
      ? (loadingProgress.loaded / loadingProgress.total) * 100 
      : 0;
    
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md px-8">
          {/* Loading Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <Building2 className="h-12 w-12 text-blue-600 animate-pulse" />
              </div>
              <Loader2 className="h-20 w-20 text-blue-400 animate-spin" />
            </div>
          </div>
          
          {/* Loading Text */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Loading Customers
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6">
            {loadingProgress.loaded > 0 ? (
              <>
                Loading customer data from database...
              </>
            ) : (
              <>Initializing...</>
            )}
          </p>
          
          {/* Progress Bar */}
          <div className="relative">
            <div className="overflow-hidden h-3 mb-2 text-xs flex rounded-full bg-gray-200">
              <div 
                style={{ width: `${progress}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-out"
              />
            </div>
            
            {/* Progress Count */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700 font-medium">
                {loadingProgress.loaded > 0 ? (
                  <>
                    {loadingProgress.loaded.toLocaleString()} 
                    {loadingProgress.estimatedTotal && (
                      <span className="text-gray-500"> of {loadingProgress.estimatedTotal.toLocaleString()}</span>
                    )}
                  </>
                ) : (
                  <>Connecting...</>
                )}
              </span>
              <span className="text-blue-600 font-semibold">
                {progress > 0 ? `${Math.round(progress)}%` : '0%'}
              </span>
            </div>
          </div>
          
          {/* Fun loading messages */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 italic">
              {progress < 25 && "Fetching customer records..."}
              {progress >= 25 && progress < 50 && "Loading contact information..."}
              {progress >= 50 && progress < 75 && "Retrieving service schedules..."}
              {progress >= 75 && progress < 100 && "Almost there..."}
              {progress >= 100 && "Finalizing..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="hub-page-header flex justify-between items-center">
        <div>
          <h1>Customers & Sites</h1>
          <p>
            {customerTab === 'active'
              ? `Manage customer companies, CSpire sites, and site information (${filteredClients.length} ${filteredClients.length === 1 ? 'customer' : 'customers'})`
              : 'Restore deactivated customers back to active directory'}
          </p>
        </div>
        {customerTab === 'active' && (
        <div className="flex space-x-3">
          <div className="relative">
            <button 
              onClick={() => setIsAdvancedToolsOpen(!isAdvancedToolsOpen)}
              className="btn btn-secondary flex items-center"
            >
              <Settings className="h-4 w-4 mr-2" />
              Advanced Tools
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${isAdvancedToolsOpen ? 'rotate-180' : ''}`} />
            </button>
            {isAdvancedToolsOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setIsAdvancedToolsOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setIsCleanupModalOpen(true);
                        setIsAdvancedToolsOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Clean Up Cities
                    </button>
                    <button
                      onClick={() => {
                        setIsAddressCleanupModalOpen(true);
                        setIsAdvancedToolsOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Clean Up Addresses
                    </button>
                    <div className="border-t border-gray-200 my-1" />
                    <GeocodeButtonDropdown />
                  </div>
                </div>
              </>
            )}
          </div>
          <button 
            onClick={handleImportClick}
            className="btn btn-secondary"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </button>
          <button 
            onClick={handleCreateClick}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </button>
        </div>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-6">
          <button
            type="button"
            onClick={() => setCustomerTab('active')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              customerTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Directory
          </button>
          <button
            type="button"
            onClick={() => setCustomerTab('deactivated')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              customerTab === 'deactivated'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Deactivated
          </button>
        </nav>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">

      {customerTab === 'deactivated' ? (
        <>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Deactivated Customers</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Customers marked as deactivated are hidden from normal views. Restore them here if needed.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deactivated customers..."
                  value={deactivatedSearch}
                  onChange={(e) => {
                    setDeactivatedSearch(e.target.value);
                    setDeactivatedPage(1);
                  }}
                  className="input pl-10"
                />
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inactiveClientsLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-8">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                        </div>
                      </td>
                    </tr>
                  ) : inactiveClientsData?.data?.clients?.length > 0 ? (
                    inactiveClientsData.data.clients.map((client) => (
                      <tr key={client.id}>
                        <td className="font-medium">{client.companyName}</td>
                        <td className="text-gray-600">
                          {[client.contactFirstName, client.contactLastName].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="text-gray-600">{client.email || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleReactivateClient(client.id, client.companyName)}
                            className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                            disabled={reactivateClientMutation.isLoading}
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span>Reactivate</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-gray-500">
                        No deactivated customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {inactiveClientsData?.data?.pagination && inactiveClientsData.data.pagination.total > 0 && (() => {
            const pagination = inactiveClientsData.data.pagination;
            const start = (pagination.page - 1) * pagination.limit + 1;
            const end = Math.min(pagination.page * pagination.limit, pagination.total);
            return (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Showing {start} to {end} of {pagination.total} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setDeactivatedPage(deactivatedPage - 1)}
                    disabled={pagination.page <= 1}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setDeactivatedPage(deactivatedPage + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      ) : (
      <>

      {/* Pending Clients Banner */}
      {pendingClients.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 shadow rounded-lg">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-900">
                  Pending Customer Approvals ({pendingClients.length})
                </h3>
              </div>
              <span className="text-xs text-amber-700">Submitted by technicians</span>
            </div>
            <div className="space-y-2">
              {pendingClients.map((pc) => (
                <div key={pc.id} className="flex items-center justify-between bg-white rounded-md border border-amber-100 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {pc.contactFirstName} {pc.contactLastName}
                      </span>
                      {pc.companyName && (
                        <span className="text-xs text-gray-500">({pc.companyName})</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {[pc.address, pc.city, pc.state, pc.zipCode].filter(Boolean).join(', ')}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {pc.phone && <span className="mr-3">Ph: {pc.phone}</span>}
                      {pc.createdBy && (
                        <span>Submitted by {pc.createdBy.firstName} {pc.createdBy.lastName}</span>
                      )}
                      {pc.notes && <span className="ml-3 italic">Note: {pc.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(pc)}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                      title="Edit before approving"
                    >
                      <Edit className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Approve ${pc.contactFirstName} ${pc.contactLastName} as a new customer?`)) {
                          approveMutation.mutate(pc.id);
                        }
                      }}
                      disabled={approveMutation.isLoading}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <UserCheck className="h-3.5 w-3.5 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Reject and delete this pending submission?`)) {
                          rejectMutation.mutate(pc.id);
                        }
                      }}
                      disabled={rejectMutation.isLoading}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                    >
                      <UserX className="h-3.5 w-3.5 mr-1" />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search and View Toggle */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            {!isCspireTab && !isFranklinTab ? (
              <div className="flex-1 relative mr-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers by name, company, location, email, phone..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (viewMode === 'table') {
                      setPage(1); // Reset to first page on search
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex-1 mr-4 text-sm text-gray-500">
                {isCspireTab ? 'Viewing CSpire sites.' : 'Viewing Franklin sites.'}
              </div>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  clearClientsSnapshot(); // force a full re-download instead of a delta sync
                  queryClient.invalidateQueries('clients');
                  queryClient.invalidateQueries('allClients');
                  toast.success('Refreshing customers...');
                }}
                disabled={isRefreshingClients}
                className="p-2 rounded-md transition-colors text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                title="Refresh customers"
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshingClients ? 'animate-spin' : ''}`} />
              </button>
              <div className="border-l border-gray-300 h-6 mx-1" />
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                title="Table view"
              >
                <List className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grouped'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                title="Grouped by city"
              >
                <Grid3x3 className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'map'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                title="Map view"
              >
                <Map className="h-5 w-5" />
              </button>
            </div>
          </div>
          {!isCspireTab && !isFranklinTab && (
            <>
              {/* Contract Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700">Filter:</span>
                <button
                  onClick={() => {
                    setContractFilter('all');
                    if (viewMode === 'table') setPage(1);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    contractFilter === 'all'
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Customers
                </button>
                <button
                  onClick={() => {
                    setContractFilter('contract');
                    if (viewMode === 'table') setPage(1);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    contractFilter === 'contract'
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Semi-Annual
                </button>
                <button
                  onClick={() => {
                    setContractFilter('annual');
                    if (viewMode === 'table') setPage(1);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    contractFilter === 'annual'
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Annual
                </button>
                <button
                  onClick={() => {
                    setContractFilter('non-contract');
                    if (viewMode === 'table') setPage(1);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    contractFilter === 'non-contract'
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Non-Contract
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Service Month Tabs */}
          <div className="px-6 pt-4 pb-2 flex justify-end border-b border-gray-200">
            <div className="flex space-x-1">
              <button
                onClick={() => {
                  setSelectedServiceMonthPair(null);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedServiceMonthPair === null
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => {
                  setSelectedServiceMonthPair('annual');
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedServiceMonthPair === 'annual'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                Annual
              </button>
              {[
                { months: [1, 7], label: 'Jan/Jul' },
                { months: [2, 8], label: 'Feb/Aug' },
                { months: [3, 9], label: 'Mar/Sep' },
                { months: [4, 10], label: 'Apr/Oct' },
                { months: [5, 11], label: 'May/Nov' },
                { months: [6, 12], label: 'Jun/Dec' }
              ].map((pair) => {
                const isSelected = selectedServiceMonthPair && 
                  Array.isArray(selectedServiceMonthPair) &&
                  selectedServiceMonthPair[0] === pair.months[0] && 
                  selectedServiceMonthPair[1] === pair.months[1];
                return (
                  <button
                    key={pair.label}
                    onClick={() => {
                      setSelectedServiceMonthPair(pair.months);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isSelected
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {pair.label}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setSelectedServiceMonthPair('commercial');
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedServiceMonthPair === 'commercial'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                Commercial
              </button>
              <button
                onClick={() => {
                  setSelectedServiceMonthPair('cspire');
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedServiceMonthPair === 'cspire'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                CSpire
              </button>
              <button
                onClick={() => {
                  setSelectedServiceMonthPair('franklin');
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedServiceMonthPair === 'franklin'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                Franklin
              </button>
            </div>
          </div>
          {isCspireTab ? (
            <div className="p-6 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="relative max-w-md w-full">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={cspireSiteSearch}
                    onChange={(e) => setCspireSiteSearch(e.target.value)}
                    placeholder="Search by site ID, location, address, or city"
                    className="pl-9 input w-full"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={cspireIncludeInactive}
                      onChange={(e) => setCspireIncludeInactive(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    Show inactive sites
                  </label>
                  <button
                    onClick={handleOpenCspireCreate}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <Plus className="h-4 w-4" />
                    Add Site
                  </button>
                </div>
              </div>

              {isCspireSitesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Site ID</th>
                        <th>Location</th>
                        <th>Address</th>
                        <th>City/State</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {cspireSites.length > 0 ? (
                        cspireSites.map((site) => (
                          <tr
                            key={site.id}
                            className="cursor-pointer hover:bg-blue-50 transition-colors"
                            onClick={() => {
                              setSelectedCspireSite(site);
                              setIsCspireDetailsModalOpen(true);
                            }}
                          >
                            <td className="font-medium">{site.siteId}</td>
                            <td className="text-gray-600">{site.locationName || '-'}</td>
                            <td className="text-gray-600">{site.addressLine1 || '-'}</td>
                            <td className="text-gray-600">
                              {[site.city, site.state, site.zipCode].filter(Boolean).join(', ') || '-'}
                            </td>
                            <td>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                site.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {site.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCspireEdit(site);
                                }}
                                className="inline-flex items-center text-blue-600 hover:text-blue-900 text-sm"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-8 text-gray-500">
                            No CSpire sites found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : isFranklinTab ? (
            <div className="p-6 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="relative max-w-md w-full">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={franklinSiteSearch}
                    onChange={(e) => setFranklinSiteSearch(e.target.value)}
                    placeholder="Search by site ID, company, location, address, city, or county"
                    className="pl-9 input w-full"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleOpenFranklinCreate}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <Plus className="h-4 w-4" />
                    Add Site
                  </button>
                </div>
              </div>

              {isFranklinSitesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Site ID</th>
                        <th>Company</th>
                        <th>Location</th>
                        <th>Address</th>
                        <th>City/State</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {franklinSites.length > 0 ? (
                        franklinSites.map((site) => (
                          <tr
                            key={site.id}
                            className="cursor-pointer hover:bg-blue-50 transition-colors"
                            onClick={() => {
                              setSelectedFranklinSite(site);
                              setIsFranklinDetailsModalOpen(true);
                            }}
                          >
                            <td className="font-medium">{site.siteId}</td>
                            <td className="text-gray-600">{site.company || '-'}</td>
                            <td className="text-gray-600">{site.locationName || '-'}</td>
                            <td className="text-gray-600">{site.address || '-'}</td>
                            <td className="text-gray-600">
                              {[site.city, site.state, site.zipCode].filter(Boolean).join(', ') || '-'}
                            </td>
                            <td>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                site.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {site.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFranklinEdit(site);
                                }}
                                className="inline-flex items-center text-blue-600 hover:text-blue-900 text-sm"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-gray-500">
                            No Franklin sites found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('companyName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Name</span>
                          <SortIcon field="companyName" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Flags
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('city')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Location</span>
                          <SortIcon field="city" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>Service Months</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                          No customers found
                        </td>
                      </tr>
                    ) : (
                      filteredClients.map((client) => {
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        const currentMonth = new Date().getMonth() + 1;
                        const isOilChangeMonth = client.isContractClient && client.oilChangeMonth === currentMonth;
                        
                        return (
                          <tr 
                            key={client.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleViewClick(client)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {client.isCommercial ? client.contactLastName : `${client.contactFirstName} ${client.contactLastName}`}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                {client.flags && client.flags.length > 0 ? (
                                  client.flags.slice(0, 5).map((flag) => (
                                    <FlagIcon
                                      key={flag.id}
                                      iconName={flag.icon}
                                      className={`h-4 w-4 ${flag.isActive ? 'text-gray-600' : 'text-gray-400'}`}
                                      title={flag.name}
                                    />
                                  ))
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                                {client.scheduleGroup && (
                                  <span
                                    className="ml-1 px-1.5 py-0.5 rounded text-2xs bg-purple-100 text-purple-800"
                                    title={`Linked group: ${client.scheduleGroup.name}`}
                                  >
                                    {client.scheduleGroup.name}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {client.city}, {client.state}
                              </div>
                            </td>
                            <td 
                              className="px-6 py-4 whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {client.isContractClient && client.serviceCycleMonths && client.serviceCycleMonths.length > 0 ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center space-x-1">
                                    {client.serviceCycleMonths.map((month, index) => {
                                      const isOilChange = client.oilChangeMonth === month;
                                      const isUpdating = updateOilChangeMonthMutation.isLoading;
                                      
                                      return (
                                        <button
                                          key={index}
                                          onClick={(e) => handleMonthClick(client, month, e)}
                                          disabled={isUpdating}
                                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                            isOilChange
                                              ? 'bg-amber-100 text-amber-800 border border-amber-300 cursor-default'
                                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:border-gray-300 border border-transparent cursor-pointer'
                                          } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          title={
                                            isOilChange
                                              ? 'Oil Change Month (click to keep)'
                                              : client.oilChangeMonth
                                                ? 'Click to set as Oil Change Month'
                                                : 'Click to set as Oil Change Month'
                                          }
                                        >
                                          {isOilChange && <Droplet className="h-3 w-3 mr-1" />}
                                          {monthNames[month - 1]}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                            <td 
                              className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleEditClick(client)}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4 inline" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    {pageSize === 'all' ? (
                      <>Showing all {filteredClients.length} results</>
                    ) : paginatedData?.data?.pagination ? (
                      <>
                        Showing {((paginatedData.data.pagination.page - 1) * paginatedData.data.pagination.limit) + 1} to{' '}
                        {Math.min(paginatedData.data.pagination.page * paginatedData.data.pagination.limit, paginatedData.data.pagination.total)} of{' '}
                        {paginatedData.data.pagination.total} results
                        {selectedServiceMonthPair && ` (filtered: ${filteredClients.length} matching)`}
                      </>
                    ) : (
                      <>Showing {filteredClients.length} results</>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        const newSize = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                        setPageSize(newSize);
                        setPage(1); // Reset to first page when changing page size
                      }}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={1000}>1000</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                </div>
                {pageSize !== 'all' && paginatedData?.data?.pagination && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={paginatedData.data.pagination.page <= 1}
                      className="btn btn-secondary btn-sm disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={paginatedData.data.pagination.page >= paginatedData.data.pagination.pages}
                      className="btn btn-secondary btn-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Grouped View */}
      {viewMode === 'grouped' && (
        <div className="space-y-4">
          {clientsByCity.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
              No customers found
            </div>
          ) : (
            clientsByCity.map((location) => (
              <div key={`${location.city}-${location.state}`} className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">
                    {location.city}, {location.state}
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({location.clients.length} {location.clients.length === 1 ? 'customer' : 'customers'})
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {location.clients.map((client) => {
                    const displayName = client.isCommercial && client.companyName 
                      ? client.companyName 
                      : `${client.contactFirstName} ${client.contactLastName}`;
                    return (
                      <div
                        key={client.id}
                        className="border border-gray-200 rounded-lg p-3 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => handleViewClick(client)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {displayName}
                            </p>
                            {client.isCommercial && (
                              <p className="text-xs text-gray-500 mt-1">
                                {client.contactFirstName} {client.contactLastName}
                              </p>
                            )}
                          <div className="flex items-center space-x-1 mt-1">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <p className="text-xs text-gray-500 truncate">
                              {client.email}
                            </p>
                          </div>
                          <div className="flex items-center space-x-1 mt-1">
                            <Phone className="h-3 w-3 text-gray-400" />
                            <p className="text-xs text-gray-500">
                              {client.phone}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {client.address}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(client);
                          }}
                          className="ml-2 text-gray-400 hover:text-blue-600"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="bg-white shadow rounded-lg p-4">
          {filteredClients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No customers to display on map
            </div>
          ) : (
            <ClientsMap
              clients={filteredClients}
              onClientClick={handleViewClick}
            />
          )}
        </div>
      )}

      {/* Geocoding Failures Section */}
      <GeocodingFailuresList />
      </>
      )}

      {/* Modals */}
      <CityCleanupModal 
        isOpen={isCleanupModalOpen} 
        onClose={() => setIsCleanupModalOpen(false)} 
      />
      
      <AddressCleanupModal
        isOpen={isAddressCleanupModalOpen}
        onClose={() => setIsAddressCleanupModalOpen(false)}
        onEditClient={(client) => {
          setIsAddressCleanupModalOpen(false);
          openEditModal(client);
        }}
      />
      
      <ClientDetailsModal
        client={selectedClient}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedClient(null);
        }}
        onEdit={handleEditFromDetails}
      />

      {cspireSiteModalMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-16 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {cspireSiteModalMode === 'create' ? 'Add CSpire Site' : 'Edit CSpire Site'}
              </h3>
              <button onClick={handleCloseCspireSiteModal} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCspireSiteFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site ID</label>
                  <input
                    type="text"
                    value={cspireFormData.siteId}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, siteId: e.target.value })}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                  <input
                    type="text"
                    value={cspireFormData.locationName}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, locationName: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={cspireFormData.addressLine1}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, addressLine1: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={cspireFormData.city}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, city: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={cspireFormData.state}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, state: e.target.value.toUpperCase() })}
                    className="input w-full"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={cspireFormData.zipCode}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, zipCode: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    value={cspireFormData.latitude}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, latitude: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    value={cspireFormData.longitude}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, longitude: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={cspireFormData.contactName}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, contactName: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={cspireFormData.contactEmail}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, contactEmail: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    value={cspireFormData.contactPhone}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, contactPhone: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <input
                    type="checkbox"
                    checked={cspireFormData.isActive}
                    onChange={(e) => setCspireFormData({ ...cspireFormData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Active site</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={cspireFormData.notes}
                  onChange={(e) => setCspireFormData({ ...cspireFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseCspireSiteModal}
                  className="btn btn-secondary"
                  disabled={cspireCreateMutation.isLoading || cspireUpdateMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={cspireCreateMutation.isLoading || cspireUpdateMutation.isLoading}
                >
                  {(cspireCreateMutation.isLoading || cspireUpdateMutation.isLoading) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {cspireSiteModalMode === 'create' ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      {cspireSiteModalMode === 'create' ? (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Site
                        </>
                      ) : (
                        <>
                          <Edit className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CspireSiteDetailsModal
        site={selectedCspireSite}
        workOrders={cspireSiteWorkOrders}
        isOpen={isCspireDetailsModalOpen}
        onClose={() => {
          setIsCspireDetailsModalOpen(false);
          setSelectedCspireSite(null);
        }}
      />

      {franklinSiteModalMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-16 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {franklinSiteModalMode === 'create' ? 'Add Franklin Site' : 'Edit Franklin Site'}
              </h3>
              <button onClick={handleCloseFranklinSiteModal} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleFranklinSiteFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site ID</label>
                  <input
                    type="text"
                    value={franklinFormData.siteId}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, siteId: e.target.value })}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={franklinFormData.company}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, company: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                  <input
                    type="text"
                    value={franklinFormData.locationName}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, locationName: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={franklinFormData.address}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, address: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={franklinFormData.city}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, city: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={franklinFormData.state}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, state: e.target.value.toUpperCase() })}
                    className="input w-full"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={franklinFormData.zipCode}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, zipCode: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                  <input
                    type="text"
                    value={franklinFormData.district}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, district: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                  <input
                    type="text"
                    value={franklinFormData.county}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, county: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <input
                    type="checkbox"
                    checked={franklinFormData.isActive}
                    onChange={(e) => setFranklinFormData({ ...franklinFormData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Active site</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={franklinFormData.notes}
                  onChange={(e) => setFranklinFormData({ ...franklinFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseFranklinSiteModal}
                  className="btn btn-secondary"
                  disabled={franklinCreateMutation.isLoading || franklinUpdateMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={franklinCreateMutation.isLoading || franklinUpdateMutation.isLoading}
                >
                  {(franklinCreateMutation.isLoading || franklinUpdateMutation.isLoading) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {franklinSiteModalMode === 'create' ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      {franklinSiteModalMode === 'create' ? (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Site
                        </>
                      ) : (
                        <>
                          <Edit className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <FranklinSiteDetailsModal
        siteId={selectedFranklinSite?.id}
        isOpen={isFranklinDetailsModalOpen}
        onClose={() => {
          setIsFranklinDetailsModalOpen(false);
          setSelectedFranklinSite(null);
        }}
      />

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Customers from CSV</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {importFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setIsImportModalOpen(false);
                      setImportFile(null);
                    }}
                    className="btn btn-secondary"
                    disabled={isImporting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportSubmit}
                    className="btn btn-primary"
                    disabled={!importFile || isImporting}
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
